import { useState, useCallback } from 'react';
import { fetchUserProfile } from '@pages/profile/store';
import authSvc from '@/services/auth/authSvc';
import clientSvc from '@/services/client/clientSvc';
import {
  UseAccountValidationProps,
  UseAccountValidationReturn,
  ValidationError,
} from '../types';
import { useNavigation } from './useNavigation';
import { useAppDispatch } from '@/store';

export const useAccountValidation = ({
  userId,
}: UseAccountValidationProps): UseAccountValidationReturn => {
  const [isActivating, setIsActivating] = useState<boolean>(false);
  const [activationError, setActivationError] =
    useState<ValidationError | null>(null);
  const appDispatch = useAppDispatch();
  const { navigateToHome } = useNavigation();

  const activateUser = useCallback(async () => {
    if (!userId) {
      setActivationError({ message: 'User ID is required for activation' });
      return;
    }

    setIsActivating(true);
    setActivationError(null);

    try {
      const { status } = await clientSvc.activateAccount(userId);

      if (status === 200) {
        const response = await authSvc.userValidate();
        appDispatch(fetchUserProfile());

        if (response.is_email_confirmed) {
          navigateToHome();
        } else {
          setActivationError({ message: 'Email confirmation failed' });
        }
      } else {
        setActivationError({
          message: 'Account activation failed',
          response: { status },
        });
      }
    } catch (error) {
      setActivationError(error as ValidationError);
    } finally {
      setIsActivating(false);
    }
  }, [userId, navigateToHome, appDispatch]);

  return {
    isActivating,
    activationError,
    activateUser,
  };
};

export default useAccountValidation;
